import { useCallback, useEffect, useRef, useState } from 'react';
import { Film, Play, Volume2 } from '../../../../icons';
import { getPlayUrl, saveProgress, type BroadcastVideoDto, type VideoSource } from '../../../../api/broadcast';
import { playTarget } from './broadcast-embed';
import { WidgetCard } from '../WidgetCard';
import { useBroadcast } from '../widget-queries';

/**
 * 사내 방송 위젯 (migration 174 · 시안 화면 F ⑥ · 화면 K 의 「위젯 연결됨」).
 *
 * ★★★ **누르기 전에는 아무 소리도 나지 않는다.** 대시보드에서 소리가 나면 사람들은 위젯 자체를
 *     지운다(시안 채널 정책의 기본값이 「자동 재생 끔」인 이유). 그래서 처음엔 썸네일만 두고,
 *     재생 주소는 **누른 뒤에** 받아 온다.
 *
 * ★★ 이어보기는 서버가 기억한다 — 기기를 옮겨도 따라온다. 되감고 싶으면 처음부터 누르면 된다.
 *
 * ⚠⚠⚠ **「자동 재생」이 켜도 아무 일이 없었다 (2026-09-09 육안 지적).**
 *   통합설정에 스위치가 있고 서버도 `autoplay` 를 저장·전달하는데, **위젯이 그 값을 안 읽었다** —
 *   판 2 에서 「켜도 아무 일 없는 스위치는 만들지 않는다」고 적어 놓고 정작 이 스위치가 그랬다.
 *
 * ★★★ **자동 재생은 «소리 없이» 다.** 사정이 둘이고 방향이 같다:
 *     ① 브라우저가 소리 나는 자동 재생을 **막는다**(muted 여야 시작된다).
 *     ② 대시보드에서 소리가 나면 사람들은 위젯을 지운다(채널 정책의 기본이 「끔」인 이유).
 *   그래서 자동으로 시작한 재생은 **음소거로 시작하고**, 「소리 켜기」를 함께 둔다 —
 *   음소거인 채 아무 말도 없으면 「소리가 고장 났다」로 읽힌다.
 */

function mmss(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function BroadcastWidget() {
  const { data, loading, error } = useBroadcast();
  const channel = data?.channel ?? null;
  const videos = data?.videos ?? [];
  const cur: BroadcastVideoDto | undefined = videos[0];

  /** 재생 주소와 «그 주소가 무엇인가» — 갈래를 모르면 그릇을 고를 수 없다. */
  const [play, setPlay] = useState<{ url: string; kind: VideoSource } | null>(null);
  /** 자동으로 시작한 재생인가 — 그때만 음소거로 연다(누른 재생은 소리가 난다). */
  const [muted, setMuted] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const lastSent = useRef(0);

  // 다른 영상으로 바뀌면 재생을 접는다 — 옛 주소가 남아 엉뚱한 것이 돌지 않게.
  useEffect(() => { setPlay(null); setMuted(false); setPlayError(null); }, [cur?.id]);

  /**
   * 채널이 「자동 재생」이면 **누르지 않아도** 시작한다 (2026-09-09).
   *
   * ★ 재생 주소는 그때그때 서명받는 것이라 여기서도 한 번 받아야 한다 — 목록 응답에는 없다.
   * ★ 실패는 조용히 접는다: 썸네일과 「재생」이 그대로 남아 손으로 누를 수 있다.
   */
  const wantsAutoplay = Boolean(channel?.autoplay);
  useEffect(() => {
    if (!wantsAutoplay || !cur) return;
    let alive = true;
    void (async () => {
      try {
        const res = await getPlayUrl(cur.id);
        if (!alive) return;
        setMuted(true);      // ★ 브라우저는 소리 나는 자동 재생을 막는다 — 음소거로 시작한다.
        setPlay(res);
      } catch {
        /* ★★★ **조용히 접지 않는다** (2026-09-09). 자동 시작이 실패하면 화면에는 종전과 같은
           썸네일만 남아, 보는 사람은 「자동 재생이 안 된다」고만 알 뿐 **왜인지 알 수 없다** —
           그 한마디 때문에 세 판을 뒤졌다. 실패는 실패라고 적고 누를 길을 남긴다. */
        if (alive) setPlayError('자동 재생을 시작하지 못했습니다 — 눌러서 재생하세요');
      }
    })();
    return () => { alive = false; };
  }, [wantsAutoplay, cur?.id]);

  const start = useCallback(async () => {
    if (!cur) return;
    try {
      const res = await getPlayUrl(cur.id);
      setMuted(false);     // 손으로 누른 재생이다 — 소리가 나야 한다.
      setPlay(res);
    } catch {
      setPlayError('지금은 재생할 수 없습니다');
    }
  }, [cur]);

  /** 본 지점 — 10초에 한 번만 보낸다(매 초 보내면 위젯 하나가 서버를 두드린다). */
  const onTime = () => {
    const el = videoRef.current;
    if (!el || !cur || !channel?.resume) return;
    const now = Math.floor(el.currentTime);
    if (now - lastSent.current < 10) return;
    lastSent.current = now;
    void saveProgress(cur.id, now).catch(() => { /* 지점을 못 적어도 재생은 이어진다 */ });
  };

  const resumeAt = cur?.positionSec ?? 0;

  /**
   * 무엇으로 틀 것인가 — **주소가 정한다**.
   *
   * ⚠⚠⚠ 종전에는 갈래를 안 보고 무조건 `<video src>` 였다. 운영에 등록된 두 편이 YouTube
   *   **페이지 주소**였고, 그것은 영상이 아니라 HTML 문서라 **아무것도 재생되지 않았다** —
   *   「자동 재생이 안 된다」의 밑에 「누르는 재생도 안 됐다」가 있었다.
   */
  const target = play
    ? playTarget(play.url, play.kind, { autoplay: true, muted, startSec: channel?.resume ? resumeAt : 0 })
    : null;

  return (
    <WidgetCard
      title="사내 방송"
      icon={Film}
      loading={loading}
      error={error}
      empty={!loading && !error && !cur ? '지금 보여줄 방송이 없습니다' : null}
      flush
    >
      {cur && (
        <div className="bcw">
          {target
            ? (
              <>
                {target.kind === 'video' && (
                  <video
                    ref={videoRef}
                    className="bcw__video"
                    src={target.src}
                    controls
                    autoPlay
                    muted={muted}
                    playsInline
                    onLoadedMetadata={(e) => {
                      // 이어보기 — 서버가 기억한 지점에서 시작한다.
                      if (resumeAt > 0) e.currentTarget.currentTime = resumeAt;
                    }}
                    onTimeUpdate={onTime}
                  />
                )}

                {/* ★★★ 끼워 넣는 재생기 — YouTube · Vimeo. `<video src>` 로는 «아무것도» 안 돈다
                       (그 주소는 영상이 아니라 HTML 문서다). 자동 재생·이어볼 지점은 주소에 실린다. */}
                {target.kind === 'embed' && (
                  <iframe
                    className="bcw__embed"
                    src={target.src}
                    title={cur.title}
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                )}

                {/* ★★ 못 트는 링크는 «못 튼다»고 말하고 원본으로 보낸다 — 검은 상자를 보여 주고
                       「고장 났나」 하게 두지 않는다. */}
                {target.kind === 'link' && (
                  <div className="bcw__nolink">
                    <div className="bcw__nolink__t">이 주소는 위젯에서 바로 재생할 수 없습니다</div>
                    <div className="bcw__nolink__s">
                      {target.host || '알 수 없는 곳'} 의 링크입니다 — YouTube · Vimeo 이거나 영상 파일 주소여야 합니다.
                    </div>
                    <a className="bcw__nolink__go" href={target.href} target="_blank" rel="noreferrer noopener">
                      원본에서 보기
                    </a>
                  </div>
                )}

                {/* ★ 음소거로 시작했다는 것을 «말해 준다» — 안 적으면 「소리가 고장 났다」로 읽힌다.
                       끼워 넣은 재생기는 우리가 음소거를 못 푸니 그 재생기 안에서 켜라고 적는다. */}
                {muted && target.kind === 'video' && (
                  <button
                    type="button"
                    className="bcw__unmute"
                    onClick={() => {
                      setMuted(false);
                      if (videoRef.current) videoRef.current.muted = false;
                    }}
                  >
                    <Volume2 size={13} aria-hidden />소리 켜기
                  </button>
                )}
                {muted && target.kind === 'embed' && (
                  <span className="bcw__unmute bcw__unmute--hint">
                    <Volume2 size={13} aria-hidden />소리 없이 재생 중 — 재생기에서 켜세요
                  </span>
                )}
              </>
            )
            : (
              <button type="button" className="bcw__poster" onClick={() => { void start(); }}>
                {cur.thumbUrl
                  ? <img className="bcw__thumb" src={cur.thumbUrl} alt="" aria-hidden />
                  : <span className="bcw__thumb bcw__thumb--none" aria-hidden />}
                <span className="bcw__play" aria-hidden><Play size={18} /></span>
                <span className="bcw__meta">
                  <b className="bcw__t">{cur.title}</b>
                  <span className="bcw__s">
                    {cur.durationSec != null && <>{mmss(cur.durationSec)}</>}
                    {/* ★ 길이를 모르면 «모른다» — 0:00 으로 적지 않는다. */}
                    {cur.durationSec == null && '길이 모름'}
                    {resumeAt > 0 && <> · 이어보기 {mmss(resumeAt)}</>}
                    {videos.length > 1 && <> · 이 채널 {videos.length}편</>}
                  </span>
                  {resumeAt > 0 && cur.durationSec
                    ? (
                      <span className="bcw__bar" aria-hidden>
                        <i style={{ width: `${Math.min(100, (resumeAt / cur.durationSec) * 100)}%` }} />
                      </span>
                    )
                    : null}
                </span>
                <span className="bcw__sr">재생</span>
              </button>
            )}
          {playError && <p className="bcw__err" role="alert">{playError}</p>}
        </div>
      )}
    </WidgetCard>
  );
}
